import moment from 'moment';
import { useMutation } from '@apollo/client';
import { useCurrency, useSelector, usePromiseQuery, useLogger } from '~Hooks';
import {
  ACTIVATE_GIFT_CARD,
  CREATE_GIFT_CARD,
  EMAIL_GIFT_CARD,
  GET_GIFT_CARDS,
  INCREASE_GIFT_CARD_BALANCE,
  REFUND_GIFT_CARD_PAYMENT,
} from '~Queries/gift-cards';
import {
  CreateGiftCard,
  CreateGiftCardVariables,
  EmailGiftCard,
  EmailGiftCardVariables,
  GetGiftCards,
  GetGiftCardsVariables,
  IncreaseGiftCardBalance,
  IncreaseGiftCardBalanceVariables,
  RefundGiftCardPayment,
  RefundGiftCardPaymentVariables,
  GetGiftCards_operativeViewer_giftcards_items as Giftcard,
  ActivateGiftCard,
  ActivateGiftCardVariables,
} from '~Queries/__queries__';
import { useGiftCardsContext } from '~Scenes/GiftCards/GiftCardsProvider';

export const useGiftCards = () => {
  const currency = useCurrency();

  const companyId = useSelector((state) => state.company);
  const companyName = useSelector((state) => state.companyName);
  const defaultLanguage = useSelector((state) => state.defaultLanguage);

  const universeId = useGiftCardsContext().universeId;
  const logger = useLogger('bae-gift-card');

  const getGiftCards = usePromiseQuery<GetGiftCards, GetGiftCardsVariables>(GET_GIFT_CARDS);

  const [doCreateGiftCard] = useMutation<CreateGiftCard, CreateGiftCardVariables>(
    CREATE_GIFT_CARD,
    {
      onCompleted: (data) => {
        logger.info('Create - Create call successful.', { data });
      },
      onError: (e) => {
        logger.error('Create - Create call error.', { errors: e });
      },
    },
  );
  const [doIncreaseBalance] = useMutation<
    IncreaseGiftCardBalance,
    IncreaseGiftCardBalanceVariables
  >(INCREASE_GIFT_CARD_BALANCE, {
    onCompleted: (data) => {
      logger.info('Deposit - Deposit call successful.', { data });
    },
    onError: (e) => {
      logger.error('Deposit - Deposit call error.', { errors: e });
    },
  });

  const [doActivateCard] = useMutation<ActivateGiftCard, ActivateGiftCardVariables>(
    ACTIVATE_GIFT_CARD,
    {
      onCompleted: (data) => {
        logger.info('Activate - Activate call successful.', { data });
      },
      onError: (e) => {
        logger.error('Activate - Activate call error.', { errors: e });
      },
    },
  );

  const [doEmailGiftCard] = useMutation<EmailGiftCard, EmailGiftCardVariables>(EMAIL_GIFT_CARD);

  const [doRefundGiftCardPayment] = useMutation<
    RefundGiftCardPayment,
    RefundGiftCardPaymentVariables
  >(REFUND_GIFT_CARD_PAYMENT);

  const print = (giftcard: Giftcard) => {
    const message = [];
    const shortCode = {
      content: giftcard.shortcode,
      fontSize: 16,
    };

    const balance = {
      content: `Balance: ${currency.format(giftcard.balance)}`,
      fontSize: 16,
    };

    const expiresAt = {
      content: `Expires: ${moment(giftcard.expiresAt).format('DD/MM/YYYY')}`,
      fontSize: 16,
    };

    const qrCode = {
      content: giftcard.shortcode,
      qr: true,
    };
    // const flavourText = {
    //   content: 'This is not a receipt',
    //   fontSize: 16,
    // };

    message.push(shortCode, balance, expiresAt, qrCode);

    // Print message on primary printer
    window.printerManager?.initiatePrint({
      data: message,
      printType: 'GIFT_CARD',
    });
  };

  const fetchGiftCards = (variables?: Partial<GetGiftCardsVariables>) => {
    if (!companyId) {
      return Promise.reject();
    }
    return getGiftCards({
      limit: 14,
      ...variables,
    });
  };

  const sendEmail = (shortcode: string, recipient: string, formattedAmount: string) => {
    return doEmailGiftCard({
      variables: {
        universeId,
        shortcode,
        email: recipient,
        emailDetails: { companyName, formattedAmount },
        lang: defaultLanguage ?? 'en',
      },
    });
  };

  const formatShortCode = (shortcode: string) => {
    // Baemingo default shortcodes to 9 characters, but we support using external codes with up to 20 characters.
    // Only format the shortcode if it's 9 characters long (formatting: AB1-23C-D45)
    if (shortcode.length === 9) {
      return shortcode.replace(/(\w{3})(\w{3})(\w{3,})/, '$1-$2-$3');
    } else {
      return shortcode;
    }
  };

  interface CreateArgs {
    emailOnCreate: string | undefined;
    initialBalance?: number;
    expiryInYears?: number;
    shouldRefetch?: true;
    predefinedShortcode: string | undefined;
  }

  const create = ({
    emailOnCreate,
    expiryInYears = 1,
    initialBalance,
    predefinedShortcode,
  }: CreateArgs) => {
    return doCreateGiftCard({
      variables: {
        universeId,
        expiryInYears,
        emailOnCreate,
        initialBalance,
        shortcode: predefinedShortcode,
        emailDetails: { companyName, formattedAmount: currency.format(initialBalance ?? 0) },
        lang: defaultLanguage ?? 'en',
      },
    });
  };

  const refundPayment = (transactionId: string, refundAmount?: number) => {
    return doRefundGiftCardPayment({
      variables: {
        transactionId,
        refundAmount,
      },
    });
  };

  const increaseBalance = (shortcode: string, amount: number, shouldRefetch = true) => {
    return doIncreaseBalance({
      variables: {
        universeId,
        shortcode,
        amount,
      },
      refetchQueries: shouldRefetch ? ['GetCompanyGiftCards'] : [],
      awaitRefetchQueries: true,
    });
  };

  const activateCard = (shortcode: string) => {
    return doActivateCard({
      variables: {
        universeId,
        shortcode,
      },
    });
  };

  return {
    print,
    sendEmail,
    formatShortCode,
    create,
    increaseBalance,
    fetchGiftCards,
    refundPayment,
    activateCard,
  };
};
